import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  UserPlus,
  Users,
  UserCircle,
  CalendarCheck,
  MapPin,
  BarChart3,
  PlusCircle,
  Eye,
  Edit,
  X,
  Filter,
  FileText,
  FileSpreadsheet,
} from "lucide-react";
import ScreenHeader from "../../components/ScreenHeader";
import "./dashboard.css";
import BackButton from "../../components/backbutton";

export default function HRDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("create-employee");
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Employee Form States
  const [employees, setEmployees] = useState([]);
  const [employeeForm, setEmployeeForm] = useState({
    employeeCode: 'EMP-' + Date.now(),
    employeeName: '',
    designation: '',
    department: '',
    category: '',
    dailyWages: '',
    overtimePerHour: '',
    monthlyWages: '',
  });

  // Extra fields state
  const [extraFields, setExtraFields] = useState([]);

  // Group employee state
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupForm, setGroupForm] = useState({
    groupName: '',
    selectedEmployee: ''
  });
  const [groupMembers, setGroupMembers] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Attendance monitor state
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().slice(0, 10));
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [attendanceTab, setAttendanceTab] = useState('present');
  const [paymentMethod, setPaymentMethod] = useState('Online');
  const [paymentReference, setPaymentReference] = useState('');

  // Assign employee location state
  const [locationForm, setLocationForm] = useState({
    selectedEmployee: '',
    locationLabel: '',
    gpsDevice: '',
    credentials: '',
    activateGps: false,
  });
  const [assignedLocations, setAssignedLocations] = useState([]);

  // Filter state
  const [filterText, setFilterText] = useState('');
  const [filteredData, setFilteredData] = useState([]);


  // Sidebar menu items
  const tabItems = [
    { id: "create-employee", label: "Create Employee", icon: <UserPlus size={18} /> },
    { id: "list-employee", label: "List Employee", icon: <Users size={18} /> },
    { id: "group-employee", label: "Group Employee", icon: <UserCircle size={18} /> },
    { id: "attendance-monitor", label: "Attendance Monitor", icon: <CalendarCheck size={18} /> },
    { id: "assign-employee-location", label: "Assign Employee Location / This Activates the GPS Attendance", icon: <MapPin size={18} /> },
    { id: "kpi", label: "KPI", icon: <BarChart3 size={18} /> }
  ];

  // Handlers for logout
  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
    navigate('/login');
  };
  const handleCancelLogout = () => setShowLogoutModal(false);

  // Handlers for extra fields
  const addExtraField = () => {
    setExtraFields([...extraFields, { name: '', value: '' }]);
  };

  const updateExtraField = (index, field, value) => {
    const updated = [...extraFields];
    updated[index][field] = value;
    setExtraFields(updated);
  };

  const removeExtraField = (index) => {
    setExtraFields(extraFields.filter((_, i) => i !== index));
  };

  // Group Employee Handlers
  const handleAddGroupMember = () => {
    if (!groupForm.selectedEmployee) return;
    const selected = employees.find(emp => emp.employeeCode === groupForm.selectedEmployee);
    if (!selected) return;
    if (groupMembers.some(member => member.employeeCode === selected.employeeCode)) {
      alert('Employee already added to group');
      return;
    }
    setGroupMembers([...groupMembers, selected]);
    setGroupForm({...groupForm, selectedEmployee: ''});
  };

  const handleRemoveGroupMember = (index) => {
    setGroupMembers(groupMembers.filter((_, i) => i !== index));
  };

  const handleCreateGroup = () => {
    if (!groupForm.groupName.trim()) {
      alert('Please provide a group name');
      return;
    }
    if (groupMembers.length === 0) {
      alert('Add at least one employee to the group');
      return;
    }
    const newGroup = {
      id: Date.now(),
      groupName: groupForm.groupName.trim(),
      members: groupMembers,
      createdAt: new Date().toISOString()
    };
    setGroups([...groups, newGroup]);
    setGroupForm({ groupName: '', selectedEmployee: '' });
    setGroupMembers([]);
    setShowGroupForm(false);
    alert('Group created successfully!');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase())
      .join('');
  };

  const handleGenerateReport = () => {
    setReportStartDate(reportStartDate || attendanceDate);
    setReportEndDate(reportEndDate || attendanceDate);
  };

  const handlePaySalary = () => {
    const presentCount = employees.filter((_, idx) => idx % 2 === 0).length;
    const salaryAmount = presentCount * 20000;
    const advanceAmount = presentCount * 2000;
    const netAmount = salaryAmount - advanceAmount;
    if (presentCount === 0) {
      alert('No salary payment available without present employees');
      return;
    }
    alert(`Salary payment of Rs. ${netAmount} marked paid via ${paymentMethod}${paymentReference ? ` (${paymentReference})` : ''}`);
    setPaymentReference('');
  };

  const handleToggleGpsActivation = () => {
    setLocationForm({
      ...locationForm,
      activateGps: !locationForm.activateGps,
    });
  };

  const handleAssignLocation = () => {
    if (!locationForm.selectedEmployee) {
      alert('Select an employee to assign location.');
      return;
    }
    if (!locationForm.locationLabel.trim()) {
      alert('Enter a location label.');
      return;
    }
    if (!locationForm.gpsDevice.trim()) {
      alert('Enter GPS device details.');
      return;
    }

    const selectedEmployee = employees.find(
      (emp) => emp.employeeCode === locationForm.selectedEmployee
    );
    if (!selectedEmployee) {
      alert('Selected employee not found.');
      return;
    }

    const assignment = {
      id: Date.now(),
      employeeCode: selectedEmployee.employeeCode,
      employeeName: selectedEmployee.employeeName,
      designation: selectedEmployee.designation,
      department: selectedEmployee.department,
      locationLabel: locationForm.locationLabel.trim(),
      gpsDevice: locationForm.gpsDevice.trim(),
      credentials: locationForm.credentials.trim() || 'Pending',
      isActive: locationForm.activateGps,
      assignedAt: new Date().toISOString(),
    };

    setAssignedLocations([...assignedLocations, assignment]);
    setLocationForm({
      selectedEmployee: '',
      locationLabel: '',
      gpsDevice: '',
      credentials: '',
      activateGps: false,
    });
    alert('Location assigned successfully. GPS attendance is activated for this employee.');
  };

  // Employee Handlers
  const handleAddEmployee = () => {
    const newEmployee = {
      id: Date.now(),
      ...employeeForm,
      extraFields: extraFields,
      createdAt: new Date().toISOString()
    };
    setEmployees([...employees, newEmployee]);
    setEmployeeForm({
      employeeCode: 'EMP-' + Date.now(),
      employeeName: '',
      designation: '',
      department: '',
      category: '',
      dailyWages: '',
      overtimePerHour: '',
      monthlyWages: '',
    });
    setExtraFields([]);
    alert('Employee added successfully!');
  };

  // Filter function
  const handleFilter = () => {
    const filtered = employees.filter(emp => 
      emp.employeeName?.toLowerCase().includes(filterText.toLowerCase()) ||
      emp.employeeCode?.toLowerCase().includes(filterText.toLowerCase()) ||
      emp.department?.toLowerCase().includes(filterText.toLowerCase()) ||
      emp.designation?.toLowerCase().includes(filterText.toLowerCase())
    );
    setFilteredData(filtered);
  };

  const handleExportPDF = () => {
    alert('Exporting to PDF...');
  };

  const handleExportExcel = () => {
    alert('Exporting to Excel...');
  };


  // Get page title based on active tab
  // const getPageTitle = () => {
  //   switch (activeTab) {
  //     case "create-employee":
  //       return "Create Employee";
  //     case "list-employee":
  //       return "List Employee";
  //     case "group-employee":
  //       return "Group Employee";
  //     case "attendance-monitor":
  //       return "Attendance Monitor";
  //     case "assign-employee-location":
  //       return "Assign Employee Location / This Activates the GPS Attendance";
  //     case "kpi":
  //       return "KPI (Key Performance Indicators)";
  //     default:
  //       return "HR Dashboard";
  //   }
  // };

  // Render Create Employee
  const renderCreateEmployee = () => (
    <div className="hr-form-container">
      <div className="hr-section-header">
        <h2 className="hr-section-title">Create Employee</h2>
      </div>

      <div className="employee-form">
          <div className="hr-form-grid">
            <div className="hr-form-group"><label>Employee Code</label><input type="text" value={employeeForm.employeeCode} disabled className="auto-field" /><small>Auto-generated</small></div>
            <div className="hr-form-group"><label>Employee Name <span className="required-star">*</span></label><input type="text" value={employeeForm.employeeName} onChange={(e) => setEmployeeForm({...employeeForm, employeeName: e.target.value})} placeholder="Enter employee name"/></div>
            <div className="hr-form-group"><label>Employee Designation <span className="required-star">*</span></label> <input type="text" value={employeeForm.designation} onChange={(e) => setEmployeeForm({...employeeForm, designation: e.target.value})} placeholder="Enter designation"/></div>
            <div className="hr-form-group"><label>Department <span className="required-star">*</span></label><input type="text" value={employeeForm.department} onChange={(e) => setEmployeeForm({...employeeForm, department: e.target.value})} placeholder="Enter department"/></div>
            <div className="hr-form-group"><label>Category <span className="required-star">*</span></label><input type="text" value={employeeForm.category} onChange={(e) => setEmployeeForm({...employeeForm, category: e.target.value})} placeholder="Enter category"/></div>
          </div>

          {/* Wages Section */}
          <div className="wages-section">
            <h3 className="wages-hr-section-title">Wages</h3>
            <div className="wages-grid">
              <div className="hr-form-group">
                <label>Daily Wages (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={employeeForm.dailyWages}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, dailyWages: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="hr-form-group">
                <label>Overtime / hr (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={employeeForm.overtimePerHour}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, overtimePerHour: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="hr-form-group">
                <label>Monthly Wages (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={employeeForm.monthlyWages}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, monthlyWages: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Extra Fields Section */}
          <div className="hr-extra-fields-section">
            <label>Add Fields</label>
            {extraFields.map((field, index) => (
              <div key={index} className="hr-extra-field-row">
                <input 
                  type="text" 
                  placeholder="Field name" 
                  value={field.name} 
                  onChange={(e) => updateExtraField(index, 'name', e.target.value)} 
                />
                <input 
                  type="text" 
                  placeholder="Field value" 
                  value={field.value} 
                  onChange={(e) => updateExtraField(index, 'value', e.target.value)} 
                />
                <button className="hr-remove-field-btn" onClick={() => removeExtraField(index)}>
                  <X size={16} />
                </button>
              </div>
            ))}
            <button className="hr-add-field-btn" onClick={addExtraField}>
              <PlusCircle size={14} /> Add Field
            </button>
          </div>

          <div className="hr-form-actions">
            <button className="hr-submit-btn" onClick={handleAddEmployee}>Submit</button>
          </div>
        </div>

      {employees.length > 0 && (
        <div className="hr-existing-data-section">
          <h3>Existing Employees ({employees.length})</h3>
          <div className="data-cards">
            {employees.map(emp => (
              <div key={emp.id} className="data-card">
                <div className="data-card-header">
                  <strong>{emp.employeeName}</strong>
                  <button className="hr-view-btn-small" onClick={() => alert(JSON.stringify(emp, null, 2))}>View</button>
                </div>
                <div className="data-card-details">
                  <p><span>Code:</span> {emp.employeeCode}</p>
                  <p><span>Designation:</span> {emp.designation || 'N/A'}</p>
                  <p><span>Department:</span> {emp.department || 'N/A'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Render List Employee
  const renderAttendanceMonitor = () => {
    const presentEmployees = employees.filter((_, idx) => idx % 2 === 0);
    const absentEmployees = employees.filter((_, idx) => idx % 2 === 1);
    const totalSalary = presentEmployees.length * 20000;
    const totalAdvance = presentEmployees.length * 2000;
    const totalNetPay = totalSalary - totalAdvance;

    return (
      <div>
        <div className="hr-section-header">
          <h2 className="hr-section-title">Attendance Monitor</h2>
          <div className="attendance-actions">
            <button className={`tab-btn ${attendanceTab === 'present' ? 'active' : ''}`} onClick={() => setAttendanceTab('present')}>Present List</button>
            <button className={`tab-btn ${attendanceTab === 'absent' ? 'active' : ''}`} onClick={() => setAttendanceTab('absent')}>Absent List</button>
            <button className={`tab-btn ${attendanceTab === 'report' ? 'active' : ''}`} onClick={() => setAttendanceTab('report')}>Custom Date Report</button>
          </div>
        </div>

        <div className="attendance-toolbar">
          <div className="date-group">
            <label>Attendance Date</label>
            <input type="date" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} />
          </div>
          <div className="date-group">
            <label>Report From</label>
            <input type="date" value={reportStartDate} onChange={(e) => setReportStartDate(e.target.value)} />
          </div>
          <div className="date-group">
            <label>Report To</label>
            <input type="date" value={reportEndDate} onChange={(e) => setReportEndDate(e.target.value)} />
          </div>
          <button className="secondary-btn" onClick={handleGenerateReport}>Generate Report</button>
        </div>

        <div className="attendance-summary-grid">
          <div className="summary-card present-card">
            <h3>Present</h3>
            <p>{presentEmployees.length} employee(s)</p>
          </div>
          <div className="summary-card absent-card">
            <h3>Absent</h3>
            <p>{absentEmployees.length} employee(s)</p>
          </div>
          <div className="summary-card report-card">
            <h3>Net Pay</h3>
            <p>Rs. {totalNetPay}</p>
          </div>
        </div>

        {attendanceTab === 'present' && (
          <div className="attendance-list-card">
            <h3>Present Employees</h3>
            {presentEmployees.length === 0 ? (
              <div className="empty-state"><p>No present employees yet.</p></div>
            ) : (
              <table className="hr-data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>Department</th>
                  </tr>
                </thead>
                <tbody>
                  {presentEmployees.map(emp => (
                    <tr key={emp.id}>
                      <td>{emp.employeeCode}</td>
                      <td>{emp.employeeName}</td>
                      <td>{emp.designation}</td>
                      <td>{emp.department}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {attendanceTab === 'absent' && (
          <div className="attendance-list-card">
            <h3>Absent Employees</h3>
            {absentEmployees.length === 0 ? (
              <div className="empty-state"><p>No absent employees found.</p></div>
            ) : (
              <table className="hr-data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>Department</th>
                  </tr>
                </thead>
                <tbody>
                  {absentEmployees.map(emp => (
                    <tr key={emp.id}>
                      <td>{emp.employeeCode}</td>
                      <td>{emp.employeeName}</td>
                      <td>{emp.designation}</td>
                      <td>{emp.department}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {attendanceTab === 'report' && (
          <div className="attendance-report-card">
            <h3>Salary Report</h3>
            <div className="report-row">
              <span>Salary Total</span>
              <strong>Rs. {totalSalary}</strong>
            </div>
            <div className="report-row">
              <span>Advance Paid</span>
              <strong>Rs. {totalAdvance}</strong>
            </div>
            <div className="report-row">
              <span>Reduced from Salary</span>
              <strong>Rs. {totalAdvance}</strong>
            </div>
            <div className="report-row net-row">
              <span>Net Pay This Month</span>
              <strong>Rs. {totalNetPay}</strong>
            </div>
            <div className="report-payment">
              <label>Paid Via</label>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <option value="Online">Online (UTR)</option>
                <option value="Cash">Cash</option>
                <option value="Check">Check</option>
              </select>
              <input
                type="text"
                placeholder="Transaction ref / UTR"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
              <button className="hr-submit-btn" onClick={handlePaySalary}>Paid</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderGroupEmployee = () => (
    <div>
      <div className="hr-section-header">
        <h2 className="hr-section-title">Group Employees</h2>
        <div className="group-header-actions">
          <button className="secondary-btn" onClick={() => setActiveTab('create-employee')}>
            Back
          </button>
          <button className={`hr-add-btn ${showGroupForm ? 'cancel-mode' : ''}`} onClick={() => setShowGroupForm(!showGroupForm)}>
            {showGroupForm ? <X size={16} /> : <><PlusCircle size={16} /> Create Group</>}
          </button>
        </div>
      </div>

      {showGroupForm && (
        <div className="group-form">
          <div className="hr-form-grid">
            <div className="hr-form-group">
              <label>Group Name</label>
              <input
                type="text"
                value={groupForm.groupName}
                onChange={(e) => setGroupForm({...groupForm, groupName: e.target.value})}
                placeholder="Enter group name"
              />
            </div>
            <div className="hr-form-group">
              <label>Select Employee</label>
              <select
                value={groupForm.selectedEmployee}
                onChange={(e) => setGroupForm({...groupForm, selectedEmployee: e.target.value})}
              >
                <option value="">Choose employee</option>
                {employees.length === 0 ? (
                  <option disabled>No result</option>
                ) : (
                  employees.map(emp => (
                    <option key={emp.employeeCode} value={emp.employeeCode}>
                      {emp.employeeName} ({emp.employeeCode})
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          <div className="group-member-actions">
            <button className="hr-add-field-btn" onClick={handleAddGroupMember}>
              <PlusCircle size={14} /> Add Employee to Group
            </button>
          </div>

          {groupMembers.length > 0 && (
            <div className="group-members">
              {groupMembers.map((member, index) => (
                <div key={member.employeeCode} className="member-chip">
                  {member.employeeName}
                  <button onClick={() => handleRemoveGroupMember(index)}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="hr-form-actions">
            <button className="hr-submit-btn" onClick={handleCreateGroup}>Group Employee</button>
            <button className="hr-cancel-btn" onClick={() => setShowGroupForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {groups.length > 0 ? (
        <div className="hr-existing-data-section">
          <div className="hr-existing-data-header">
            <h3>Existing Groups</h3>
            <span>{groups.length} groups</span>
          </div>
          <div className="hr-group-grid">
            {groups.map(group => (
              <div key={group.id} className="hr-group-card">
                <div className="hr-group-card-top">
                  <div>
                    <h4>{group.groupName}</h4>
                    <p className="hr-group-meta">
                      {group.members.length} employee{group.members.length !== 1 ? 's' : ''} · Created {new Date(group.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button className="hr-view-btn-small" onClick={() => setSelectedGroup(group)}>View</button>
                </div>
                <div className="hr-group-card-body">
                  <div className="hr-group-avatars">
                    {group.members.slice(0, 3).map(member => (
                      <div key={member.employeeCode} className="hr-group-avatar">
                        {getInitials(member.employeeName)}
                      </div>
                    ))}
                    {group.members.length > 3 && (
                      <div className="hr-group-avatar hr-more-count">+{group.members.length - 3}</div>
                    )}
                  </div>
                  <div className="hr-group-members-wrap">
                    {group.members.map(member => (
                      <span key={member.employeeCode} className="hr-group-member-chip">
                        {member.employeeName}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {selectedGroup && (
            <div className="hr-group-detail-card">
              <div className="hr-group-detail-header">
                <div>
                  <h3>{selectedGroup.groupName}</h3>
                  <p className="hr-group-meta">
                    {selectedGroup.members.length} employee{selectedGroup.members.length !== 1 ? 's' : ''} · Created {new Date(selectedGroup.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <button className="hr-detail-close-btn" onClick={() => setSelectedGroup(null)}>Close</button>
              </div>
              <div className="hr-group-detail-body">
                <p className="hr-group-detail-text">
                  This group is displayed inside its own card for clearer review of members and group details.
                </p>
                <div className="hr-group-members-wrap">
                  {selectedGroup.members.map(member => (
                    <span key={member.employeeCode} className="hr-group-member-chip">
                      {member.employeeName}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="empty-state">
          <p>No groups created yet.</p>
          <small>Create a group using the button above</small>
        </div>
      )}
    </div>
  );

  const renderListEmployee = () => (
    <div>
      <div className="hr-section-header">
        <h2 className="hr-section-title">List Employee</h2>
        <div className="hr-filter-export-section">
          <div className="hr-filter-group">
            <input 
              type="text" 
              placeholder="Filter by name, code, department..." 
              value={filterText} 
              onChange={(e) => setFilterText(e.target.value)} 
              className="hr-filter-input" 
            />
            <button className="hr-filter-btn" onClick={handleFilter}>
              <Filter size={16} /> Filter
            </button>
          </div>
          <div className="hr-export-buttons">
            <button className="hr-export-btn pdf" onClick={handleExportPDF}>
              <FileText size={16} /> PDF
            </button>
            <button className="hr-export-btn excel" onClick={handleExportExcel}>
              <FileSpreadsheet size={16} /> Excel
            </button>
          </div>
        </div>
      </div>

      {employees.length === 0 && filteredData.length === 0 ? (
        <div className="empty-state">
          <Users size={48} color="#CBD5E1" />
          <p>No employees available</p>
          <small>Go to "Create Employee" to add employees</small>
        </div>
      ) : (
        <table className="hr-data-table">
          <thead>
            <tr>
              <th>Employee Code</th>
              <th>Employee Name</th>
              <th>Designation</th>
              <th>Department</th>
              <th>Category</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(filteredData.length > 0 ? filteredData : employees).map(emp => (
              <tr key={emp.id}>
                <td>{emp.employeeCode}</td>
                <td>{emp.employeeName}</td>
                <td>{emp.designation}</td>
                <td>{emp.department}</td>
                <td>{emp.category}</td>
                <td>
                  <button className="hr-action-icon" onClick={() => alert(JSON.stringify(emp, null, 2))}>
                    <Eye size={16} />
                  </button>
                  <button className="hr-action-icon edit" onClick={() => alert('Edit: ' + emp.employeeName)}>
                    <Edit size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  // Render placeholder for other tabs
  const renderAssignLocation = () => {
    const assignedCount = assignedLocations.length;
    const activeGpsCount = assignedLocations.filter((item) => item.isActive).length;
    const inactiveCount = assignedCount - activeGpsCount;

    return (
      <div >
        <div className="hr-section-header">
          <div>
            <h2 className="hr-section-title">Assign Employee Location</h2>
          </div>
          <button className="secondary-btn" onClick={() => setActiveTab('attendance-monitor')}>
            View Attendance
          </button>
        </div>

        <div className="assign-hr-form-grid">
          <div className="hr-form-group">
            <label>Employee</label>
            <select
              value={locationForm.selectedEmployee}
              onChange={(e) => setLocationForm({ ...locationForm, selectedEmployee: e.target.value })}
            >
              <option value="">Choose employee</option>
              {employees.length === 0 ? (
                <option disabled>No result</option>
              ) : (
                employees.map((emp) => (
                  <option key={emp.employeeCode} value={emp.employeeCode}>
                    {emp.employeeName} ({emp.employeeCode})
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="hr-form-group">
            <label>Location Label</label>
            <input
              type="text"
              value={locationForm.locationLabel}
              onChange={(e) => setLocationForm({ ...locationForm, locationLabel: e.target.value })}
              placeholder="e.g. Main Warehouse, Site A"
            />
          </div>

          <div className="hr-form-group">
            <label>GPS Device ID</label>
            <input
              type="text"
              value={locationForm.gpsDevice}
              onChange={(e) => setLocationForm({ ...locationForm, gpsDevice: e.target.value })}
              placeholder="Enter device or tracker ID"
            />
          </div>

          <div className="hr-form-group">
            <label>Attendance Credentials</label>
            <input
              type="text"
              value={locationForm.credentials}
              onChange={(e) => setLocationForm({ ...locationForm, credentials: e.target.value })}
              placeholder="Enter GPS token or credentials"
            />
          </div>
        </div>

        <div className="assign-actions">
          <button
            className={`toggle-btn ${locationForm.activateGps ? 'active' : ''}`}
            onClick={handleToggleGpsActivation}
          >
            {locationForm.activateGps ? 'GPS Attendance Active' : 'Activate GPS Attendance'}
          </button>
          <button className="hr-submit-btn" onClick={handleAssignLocation}>
            Assign Location
          </button>
        </div>

        <div className="location-summary-grid">
          <div className="location-card">
            <h3>Assigned Locations</h3>
            <p>{assignedCount}</p>
          </div>
          <div className="location-card">
            <h3>Active GPS</h3>
            <p>{activeGpsCount}</p>
          </div>
          <div className="location-card">
            <h3>Pending Activation</h3>
            <p>{inactiveCount}</p>
          </div>
        </div>

        {assignedLocations.length === 0 ? (
          <div className="empty-state">
            <MapPin size={48} color="#CBD5E1" />
            <p>No assigned locations yet.</p>
            <small>Assign an employee location to activate GPS attendance.</small>
          </div>
        ) : (
          <div className="attendance-list-card">
            <h3>Assigned Locations</h3>
            <table className="hr-data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Location</th>
                  <th>GPS Device</th>
                  <th>Status</th>
                  <th>Credentials</th>
                </tr>
              </thead>
              <tbody>
                {assignedLocations.map((assignment) => (
                  <tr key={assignment.id}>
                    <td>{assignment.employeeName}</td>
                    <td>{assignment.locationLabel}</td>
                    <td>{assignment.gpsDevice}</td>
                    <td>
                      <span className={`location-status-pill ${assignment.isActive ? 'active' : 'inactive'}`}>
                        {assignment.isActive ? 'Active' : 'Waiting'}
                      </span>
                    </td>
                    <td>{assignment.credentials}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderKpi = () => {
    const kpiMetrics = [
      { title: 'Attendance performance', value: '92%', caption: 'Punctuality and presence' },
      { title: 'Production performance', value: '86%', caption: 'Targets completed' },
      { title: 'Quality performance', value: '89%', caption: 'Defect-free work ratio' },
      { title: 'Overtime performance', value: '74%', caption: 'Overtime efficiency' },
      { title: 'Overall performance', value: '88%', caption: 'Weighted composite score' }
    ];

    const employeeKpis = employees.map((emp, index) => {
      const attendance = 80 + (index * 4) % 15;
      const production = 70 + (index * 5) % 20;
      const quality = 75 + (index * 6) % 18;
      const overtime = 8 + (index * 3) % 18;
      const overall = Math.round((attendance + production + quality) / 3);
      return {
        ...emp,
        attendance,
        production,
        quality,
        overtime,
        overall,
      };
    });

    return (
      <div className="">
        <div className="hr-section-header">
          <div>
            <h2 className="hr-section-title">Key Performance Indicator</h2>
          </div>
        </div>

        <div className="kpi-grid">
          {kpiMetrics.map((metric) => (
            <div key={metric.title} className="kpi-card">
              <div className="kpi-card-top">
                <h3>{metric.title}</h3>
                <span className="kpi-value">{metric.value}</span>
              </div>
              <p>{metric.caption}</p>
              <div className="kpi-progress">
                <div className="kpi-progress-bar" style={{ width: metric.value }} />
              </div>
            </div>
          ))}
        </div>

        <div className="hr-section-header">
          <h2 className="hr-section-title">List Of Employee</h2>
        </div>

        {employees.length === 0 ? (
          <div className="empty-state">
            <p>No employees available to compute KPI metrics.</p>
            <small>Go to Create Employee and add employee data first.</small>
          </div>
        ) : (
          <div className="kpi-table-card">
            <table className="hr-data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Designation</th>
                  <th>Department</th>
                  <th>Attendance %</th>
                  <th>Production %</th>
                  <th>Quality %</th>
                  <th>Overtime %</th>
                  <th>Overall %</th>
                </tr>
              </thead>
              <tbody>
                {employeeKpis.map((emp) => (
                  <tr key={emp.id}>
                    <td>{emp.employeeName}</td>
                    <td>{emp.designation || 'N/A'}</td>
                    <td>{emp.department || 'N/A'}</td>
                    <td>{emp.attendance}%</td>
                    <td>{emp.production}%</td>
                    <td>{emp.quality}%</td>
                    <td>{emp.overtime}%</td>
                    <td>{emp.overall}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // const renderOtherTab = () => (
  //   <div className="placeholder-content">
  //     <p>Content for {getPageTitle()} coming soon...</p>
  //   </div>
  // );

  // Main content renderer with switch statement
  const renderContent = () => {
    switch(activeTab) {
      case "create-employee":
        return renderCreateEmployee();
      case "list-employee":
        return renderListEmployee();
      case "group-employee":
        return renderGroupEmployee();
      case "attendance-monitor":
        return renderAttendanceMonitor();
      case "assign-employee-location":
        return renderAssignLocation();
      case "kpi":
        return renderKpi();
      default:
        return renderCreateEmployee();
    }
  };

  return (
    <div className="hr-dashboard-container">
      {/* Sidebar */}
      <div className="hr-sidebar">
        <div className="hr-logo-container">
          <div className="hr-logo-icon">
            <img 
              src="/rahulerp.jpg" 
              alt="Company Logo" 
              className="hr-logo-image"
            />
          </div>
          <div>
            <h2 className="hr-company-name">Ragul Auto Tech</h2>
            <p className="hr-company-tagline">AI Powered ERP</p>
          </div>
        </div>

        <nav className="hr-nav-menu">
          {tabItems.map((tab) => (
            <button
              key={tab.id}
              className={`hr-nav-item ${activeTab === tab.id ? 'hr-nav-item-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="hr-nav-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="hr-logout-container">
          <button onClick={handleLogoutClick} className="hr-logout-button">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="hr-main-content">
        {/* Header */}
        <ScreenHeader
          title="Human Resources Dashboard"
          description={`Welcome, ${user?.role || 'HR Manager'}`}
          timestamp={new Date().toLocaleString()}
          containerClassName="hr-header"
          titleClassName="hr-main-title"
          descriptionClassName="hr-subtitle"
          actionsClassName="hr-header-actions"
          timestampClassName="hr-last-updated"
        />

        <div className="hr-page-body">
          <BackButton />

          {/* Content Area - Render based on active tab */}
          <div className="hr-content-card">
            {renderContent()}
          </div>
        </div>

        {showLogoutModal && (
          <div className="logout-modal-overlay">
            <div className="logout-modal">
              <div className="logout-modal-header">
                <div className="logout-modal-icon">👋</div>
                <h3>Confirm Logout</h3>
              </div>
              <div className="logout-modal-body">
                <p>Are you sure you want to logout from <strong>Human Resources (HR)</strong> dashboard?</p>
                <p className="logout-modal-warning">You will be redirected to the login page.</p>
              </div>
              <div className="logout-modal-footer">
                <button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button>
                <button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
